/**
 * Minimap HUD — top-right corner overlay showing city layout + player position.
 *
 * Why an HTML canvas instead of a Phaser scene?
 *   The city is 10240×6400px. A Phaser camera/scene minimap means a second
 *   render of every game object at 1/40 scale, which is wasteful when the
 *   city geometry is static. Drawing the static layer (roads, plaza,
 *   buildings) once into a small <canvas> and only repainting the player
 *   dot each frame is cheaper and easier to style.
 *
 * Architecture:
 *   - constructor() creates a fixed-position canvas in the top-right.
 *   - paintStatic(ctx) is called ONCE with city geometry → cached as a
 *     bitmap so we never redraw it.
 *   - updatePlayer(wx, wy) and updateRemotes(positions) repaint just the
 *     dot layer each tick.
 */

interface BuildingRect {
  col: number; row: number; cols: number; rows: number;
  themeColor: number; name: string;
}

interface MinimapConfig {
  mapTileCols: number;     // total map cols in tiles
  mapTileRows: number;     // total map rows in tiles
  tilePx: number;          // pixels per tile in the world
  buildings: BuildingRect[];
  roadRows: number[];      // tile rows that are roads
  sideRows: number[];      // tile rows that are sidewalks
  plaza: { x0: number; y0: number; x1: number; y1: number };  // tile coords
  vRoadCols?: number[];    // optional vertical road tile cols
}

export class Minimap {
  private root: HTMLDivElement;
  private mapCanvas: HTMLCanvasElement;     // static city geometry (cached)
  private overlayCanvas: HTMLCanvasElement; // player + remote dots (per-frame)
  private mapCtx: CanvasRenderingContext2D;
  private overlayCtx: CanvasRenderingContext2D;
  private cfg!: MinimapConfig;
  private widthPx: number;
  private heightPx: number;
  private localDot: { x: number; y: number } = { x: 0, y: 0 };
  private remoteDots: Map<string, { x: number; y: number; color: string }> = new Map();

  constructor(widthPx: number = 240) {
    this.widthPx = widthPx;
    // Aspect ratio determined by the world later; default to 4:3 for sizing.
    this.heightPx = Math.round(widthPx * 0.625);

    this.root = document.createElement("div");
    this.root.id = "minimap";
    this.root.style.cssText = `
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 5;
      pointer-events: none;
      width: ${widthPx}px;
      padding: 8px;
      border-radius: 12px;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(8px) saturate(140%);
      -webkit-backdrop-filter: blur(8px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.18);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      font-family: Inter, system-ui, sans-serif;
    `;

    const title = document.createElement("div");
    title.textContent = "♥ City Map";
    title.style.cssText = `
      color: #f1f5f9;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 6px;
      text-align: center;
      opacity: 0.85;
    `;
    this.root.appendChild(title);

    const stack = document.createElement("div");
    stack.style.cssText = `position: relative; width: ${widthPx - 16}px;`;

    this.mapCanvas = document.createElement("canvas");
    this.mapCanvas.style.cssText = `
      display: block;
      width: 100%;
      border-radius: 6px;
      image-rendering: pixelated;
    `;
    stack.appendChild(this.mapCanvas);

    this.overlayCanvas = document.createElement("canvas");
    this.overlayCanvas.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 100%;
      pointer-events: none;
      image-rendering: pixelated;
    `;
    stack.appendChild(this.overlayCanvas);

    this.root.appendChild(stack);
    document.body.appendChild(this.root);

    this.mapCtx = this.mapCanvas.getContext("2d")!;
    this.overlayCtx = this.overlayCanvas.getContext("2d")!;
  }

  /** Paint static city geometry once. Recomputes canvas height from aspect. */
  paintStatic(cfg: MinimapConfig): void {
    this.cfg = cfg;
    const aspect = cfg.mapTileRows / cfg.mapTileCols;
    this.heightPx = Math.round((this.widthPx - 16) * aspect);
    const w = this.widthPx - 16;
    const h = this.heightPx;
    this.mapCanvas.width = w;
    this.mapCanvas.height = h;
    this.overlayCanvas.width = w;
    this.overlayCanvas.height = h;
    this.overlayCanvas.style.height = `${h}px`;
    this.mapCanvas.style.height = `${h}px`;

    const ctx = this.mapCtx;
    // Grass
    ctx.fillStyle = "#4d7c2f";
    ctx.fillRect(0, 0, w, h);
    // Color noise for grass texture
    ctx.fillStyle = "rgba(86, 136, 56, 0.5)";
    for (let i = 0; i < 80; i++) {
      ctx.fillRect(Math.random() * w, Math.random() * h, 2, 2);
    }
    // Roads (horizontal asphalt)
    ctx.fillStyle = "#2f3640";
    const tilePxX = w / cfg.mapTileCols;
    const tilePxY = h / cfg.mapTileRows;
    for (const r of cfg.roadRows) {
      ctx.fillRect(0, r * tilePxY, w, tilePxY);
    }
    // Vertical roads
    if (cfg.vRoadCols) {
      for (const c of cfg.vRoadCols) {
        ctx.fillRect(c * tilePxX, 0, tilePxX, h);
      }
    }
    // Sidewalks (lighter)
    ctx.fillStyle = "#b8b8b0";
    for (const r of cfg.sideRows) {
      ctx.fillRect(0, r * tilePxY, w, tilePxY);
    }
    // Plaza
    const px0 = cfg.plaza.x0 * tilePxX;
    const py0 = cfg.plaza.y0 * tilePxY;
    const pw = (cfg.plaza.x1 - cfg.plaza.x0) * tilePxX;
    const ph = (cfg.plaza.y1 - cfg.plaza.y0) * tilePxY;
    ctx.fillStyle = "#ec4899";
    ctx.fillRect(px0, py0, pw, ph);
    // Plaza heart
    ctx.fillStyle = "#fff";
    ctx.font = `bold ${Math.max(8, Math.round(ph * 0.45))}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("♥", px0 + pw / 2, py0 + ph / 2);
    // Buildings
    for (const b of cfg.buildings) {
      const bx = b.col * tilePxX;
      const by = b.row * tilePxY;
      const bw = b.cols * tilePxX;
      const bh = b.rows * tilePxY;
      const hex = "#" + b.themeColor.toString(16).padStart(6, "0");
      ctx.fillStyle = hex;
      ctx.fillRect(bx, by, bw, bh);
      // Inner darker tone for depth
      ctx.fillStyle = "rgba(0, 0, 0, 0.18)";
      ctx.fillRect(bx, by + bh - Math.max(1, bh * 0.18), bw, Math.max(1, bh * 0.18));
      // Outline
      ctx.strokeStyle = "rgba(255, 255, 255, 0.35)";
      ctx.lineWidth = 0.6;
      ctx.strokeRect(bx + 0.5, by + 0.5, bw - 1, bh - 1);
    }
    // Outer frame
    ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }

  updatePlayer(worldX: number, worldY: number): void {
    if (!this.cfg) return;
    this.localDot = { x: worldX, y: worldY };
    this.repaintOverlay();
  }

  updateRemotes(remotes: Array<{ id: string; x: number; y: number; color: string }>): void {
    if (!this.cfg) return;
    this.remoteDots.clear();
    for (const r of remotes) {
      this.remoteDots.set(r.id, { x: r.x, y: r.y, color: r.color });
    }
    this.repaintOverlay();
  }

  private repaintOverlay(): void {
    const ctx = this.overlayCtx;
    const w = this.overlayCanvas.width;
    const h = this.overlayCanvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!this.cfg) return;
    const tilePxX = w / this.cfg.mapTileCols;
    const tilePxY = h / this.cfg.mapTileRows;
    const worldToMapX = (wx: number) => (wx / this.cfg.tilePx) * tilePxX;
    const worldToMapY = (wy: number) => (wy / this.cfg.tilePx) * tilePxY;
    // Remote dots first (so local dot draws on top)
    for (const dot of this.remoteDots.values()) {
      const mx = worldToMapX(dot.x);
      const my = worldToMapY(dot.y);
      ctx.fillStyle = dot.color;
      ctx.beginPath();
      ctx.arc(mx, my, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    // Local dot — bright pink with halo
    const lx = worldToMapX(this.localDot.x);
    const ly = worldToMapY(this.localDot.y);
    ctx.fillStyle = "rgba(255, 46, 136, 0.35)";
    ctx.beginPath();
    ctx.arc(lx, ly, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ff2e88";
    ctx.beginPath();
    ctx.arc(lx, ly, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  destroy(): void {
    this.root.remove();
  }
}
